/**
 * AddNewContactsModal
 * ───────────────────
 * Voeg contacten die nu in segment vallen maar nog niet in queue zitten
 * toe aan een lopende sequence-campagne. Preview eerst → bevestig → uitvoeren.
 *
 * Endpoints:
 *   GET  /api/email/campaigns/{cid}/add-new-contacts/preview
 *   POST /api/email/campaigns/{cid}/add-new-contacts
 */
import React, { useEffect, useState } from 'react';
import { X, UserPlus, CheckCircle, Warning, CircleNotch } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const Stat = ({ label, value, color = 'text-zinc-900', sub }) => (
  <div className="flex items-baseline justify-between py-1.5 border-b border-zinc-100 last:border-0">
    <span className="text-xs text-zinc-600">{label}</span>
    <span className="text-right">
      <span className={`font-mono font-bold ${color}`}>{value}</span>
      {sub && <span className="text-[10px] text-zinc-400 ml-1.5">{sub}</span>}
    </span>
  </div>
);

export default function AddNewContactsModal({ campaign, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const fetchPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/email/campaigns/${campaign.campaign_id}/add-new-contacts/preview`, {
        credentials: 'include',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Preview mislukt');
      setPreview(d);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => { if (campaign) fetchPreview(); }, [campaign]);  // eslint-disable-line

  const runAdd = async () => {
    setRunning(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/email/campaigns/${campaign.campaign_id}/add-new-contacts`, {
        method: 'POST', credentials: 'include',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Toevoegen mislukt');
      setResult(d);
      if (onDone) onDone(d);
    } catch (e) {
      setError(e.message);
    }
    setRunning(false);
  };

  if (!campaign) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
      data-testid="add-new-contacts-modal"
    >
      <div
        className="bg-white w-full max-w-md sm:max-w-lg max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col rounded-sm shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-zinc-200">
          <div className="min-w-0 flex-1 pr-2">
            <h2 className="text-base sm:text-lg font-bold text-zinc-900 flex items-center gap-2">
              <UserPlus size={18} weight="bold" className="text-emerald-600 flex-shrink-0" />
              <span className="truncate">Contacten toevoegen</span>
            </h2>
            <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{campaign.name}</div>
          </div>
          <button
            onClick={onClose}
            className="p-2 -mr-2 hover:bg-zinc-100 active:bg-zinc-200 rounded-sm flex-shrink-0"
            data-testid="add-new-contacts-close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading && (
            <div className="text-center text-sm text-zinc-500 py-8 flex items-center justify-center gap-2">
              <CircleNotch size={16} className="animate-spin" /> Segment opnieuw evalueren…
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2 rounded-sm flex items-start gap-2 mb-3" data-testid="add-new-contacts-error">
              <Warning size={16} weight="fill" className="text-red-600 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Preview state */}
          {preview && !result && !loading && (
            <>
              <div className="mb-4">
                <div className="text-center py-4 sm:py-6 bg-emerald-50 border border-emerald-200 rounded-sm" data-testid="add-new-count-preview">
                  <div className="text-3xl sm:text-4xl font-bold text-emerald-700 font-mono">{preview.would_add_contacts}</div>
                  <div className="text-xs text-emerald-700/80 mt-1">
                    nieuwe contact{preview.would_add_contacts === 1 ? '' : 'en'}
                  </div>
                  <div className="text-[11px] text-emerald-600/70 mt-0.5">
                    {preview.would_queue_items} extra e-mails (6 stappen per contact)
                  </div>
                </div>
              </div>

              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1.5">Breakdown</div>
              <div className="bg-white border border-zinc-200 rounded-sm px-3 py-1">
                <Stat label="Totaal in segment" value={preview.total_in_segment} />
                <Stat label="Al in campagne" value={preview.skipped_existing} color="text-zinc-500" sub="(skip)" />
                <Stat label="Uitgeschreven" value={preview.skipped_unsubscribed} color="text-amber-700" sub="(skip)" />
                <Stat label="Geblokkeerd domein" value={preview.skipped_blocked} color="text-red-700" sub="(skip)" />
                {preview.skipped_duplicate > 0 && (
                  <Stat label="Dubbele e-mails" value={preview.skipped_duplicate} color="text-zinc-500" sub="(skip)" />
                )}
              </div>

              {preview.would_add_contacts === 0 && (
                <div className="mt-4 text-xs text-zinc-500 italic text-center">
                  Geen nieuwe contacten gevonden — alle leads in dit segment zitten al in de campagne of zijn uitgeschreven.
                </div>
              )}
            </>
          )}

          {/* Result state */}
          {result && (
            <div className="text-center py-6" data-testid="add-new-contacts-result">
              <CheckCircle size={36} weight="fill" className="text-emerald-600 mx-auto mb-2" />
              <div className="text-lg font-bold text-zinc-900 mb-1">
                {result.added_contacts} contact{result.added_contacts === 1 ? '' : 'en'} toegevoegd
              </div>
              <div className="text-xs text-zinc-500 mb-4">
                {result.queued_items} e-mails klaargezet in {6} stappen
              </div>
              <div className="bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2 text-left">
                <Stat label="Overgeslagen — al in campagne" value={result.skipped_existing} color="text-zinc-500" />
                <Stat label="Overgeslagen — uitgeschreven" value={result.skipped_unsubscribed} color="text-zinc-500" />
                <Stat label="Overgeslagen — geblokkeerd" value={result.skipped_blocked} color="text-zinc-500" />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 border-t border-zinc-200 flex gap-2 justify-end bg-zinc-50">
          {!result && (
            <>
              <button
                onClick={onClose}
                disabled={running}
                className="px-4 py-2 text-sm font-medium border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-100 active:bg-zinc-200 rounded-sm transition-colors disabled:opacity-50"
                data-testid="add-new-contacts-cancel"
              >
                Annuleer
              </button>
              <button
                onClick={runAdd}
                disabled={running || !preview || (preview?.would_add_contacts ?? 0) === 0}
                className="px-4 py-2 text-sm font-bold bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 rounded-sm transition-colors disabled:opacity-50 flex items-center gap-2"
                data-testid="add-new-contacts-confirm"
              >
                {running && <CircleNotch size={14} className="animate-spin" />}
                {running ? 'Toevoegen…' : `Voeg ${preview?.would_add_contacts ?? 0} toe`}
              </button>
            </>
          )}
          {result && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium bg-zinc-900 text-white hover:bg-black rounded-sm"
              data-testid="add-new-contacts-done"
            >
              Sluiten
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
